import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { searchClient } from "@/meilisearch";
import Logger from "@cappelendamm/logger";
import { ItemsIndex } from "@cappelendamm/catalogue/meilisearch/indexes.js";
import {
  FacetDistribution,
  FacetHit,
  Filter,
  MultiSearchParams,
  MultiSearchQuery,
} from "meilisearch";
import {
  getFacetFromFacetConfig,
  removeFilterByAttribute,
} from "./search-filter-selectors";
import { encodeSearch } from "./utils/encode-search";
import { FacetConfig, LOAD_MORE_BATCH_SIZE } from "./constants";

/**
 * We want to use array instead of working complex strings
 * https://www.meilisearch.com/docs/reference/api/search/search-with-post#body-filter
 */
export type Filters = Exclude<Filter, string>;

export type Search = {
  query?: string;
  filters?: Filters;
  offset?: number;
  limit?: number;
  attributesToRetrieve?: string[];
  facets?: FacetConfig[];
  distinct?: string;
};

export type SearchResult = {
  results: IndexDocument[];
  nbHits: number;
  facets: FacetValues;
  searchId: string;
};

type FacetValues = Record<string, FacetHit[]>;

type FacetSearch = {
  facetName: string;
  facetQuery?: string;
};

export async function getFacetSearchResults({
  facets,
  filters,
  query,
  ...search
}: FacetSearch & Search): Promise<FacetHit[]> {
  try {
    const response = await searchClient
      .index<IndexDocument>(ItemsIndex.name)
      .searchForFacetValues({
        ...search,
        q: query,
        facets: getFacetFromFacetConfig(facets, filters),
        filter: filters,
      });
    return response.facetHits;
  } catch (e) {
    Logger.error(`Meilisearch facet search failed \n${e}`);
    return [];
  }
}

export async function getFacetedSearchResults(
  search: Search,
): Promise<SearchResult> {
  const {
    query,
    filters,
    offset = 0,
    limit = LOAD_MORE_BATCH_SIZE,
    attributesToRetrieve,
    facets: providedFacets,
    distinct,
  } = search;
  try {
    const facets = getFacetFromFacetConfig(providedFacets, filters);

    const searchQuery: MultiSearchQuery = {
      q: query ?? "",
      indexUid: ItemsIndex.name,
      filter: filters,
      sort: ["immediacyScore:desc"],
      offset,
      limit,
      attributesToRetrieve,
      facets,
      distinct,
    };

    const orFilters =
      filters?.filter((filter) => typeof filter !== "string") ?? [];

    const orFacetQueries: MultiSearchQuery[] = orFilters.reduce<
      MultiSearchQuery[]
    >((acc, f) => {
      const facet = facets.find((facet) =>
        f.some((filter) => filter.includes(facet)),
      );
      if (!facet) return acc;
      return [
        ...acc,
        {
          q: query ?? "",
          indexUid: ItemsIndex.name,
          filter: removeFilterByAttribute(filters, facet),
          sort: ["immediacyScore:desc"],
          offset,
          limit: 1,
          attributesToRetrieve,
          facets: [facet],
          distinct,
        },
      ];
    }, []);

    const response = await searchClient.multiSearch<
      MultiSearchParams,
      IndexDocument
    >({
      queries: [searchQuery, ...orFacetQueries],
    });

    const [results, ...facetResults] = response.results;

    const mappedFacets = Object.entries({
      ...results?.facetDistribution,
      ...facetResults?.reduce<FacetDistribution>(
        (acc, cur) => ({ ...acc, ...cur.facetDistribution }),
        {},
      ),
    }).reduce<FacetValues>((acc, [attribute, values]) => {
      acc[attribute] = Object.entries(values)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count);
      return acc;
    }, {});

    const facetNbHits = facetResults?.reduce<number[]>(
      (filtered, value) =>
        value.estimatedTotalHits
          ? [...filtered, value.estimatedTotalHits]
          : filtered,
      [],
    );

    const nbHits =
      results?.estimatedTotalHits ??
      (facetNbHits.length > 0
        ? Math.min(...facetNbHits)
        : (results?.hits.length ?? 0));

    return {
      results: results?.hits ?? [],
      nbHits,
      facets: mappedFacets,
      searchId: encodeSearch(search),
    };
  } catch (e) {
    Logger.error(`Meilisearch call failed \n${e}`);
    return {
      results: [],
      nbHits: 0,
      facets: {},
      searchId: encodeSearch(search),
    };
  }
}
